import React, {
	CSSProperties,
	ReactNode,
	useEffect,
	useMemo,
	useState,
} from 'react'
import { AnnotatedMarkdown } from './Markdown'
import {
	deleteAnnotationRange,
	getRangeIntersections,
	Annotation,
	BUFFER_COLOR,
	AVAILABLE_COLORS,
	mixColors,
	Annotations,
} from './helpers'
import Modal from '../modal'
import { getSingleAnnotationStyle, NO_COLOR } from './annotationStyling'
import { DashboardModalBody, DashboardModalHeader } from '../modal/dashboard'
import { Button } from '@mission.io/styles'
import { CloseAnnotationTypes } from '@mission.io/question-views'
import { FaCheckCircle, FaTrash } from 'react-icons/fa'
import classNames from 'classnames'
import {
	CLOSE_READING,
	CLOSE_ANNOTATION_HELPERS,
	Range,
} from '@mission.io/question-toolkit'
import ConfirmationModal from '../modal/Confirmation'
import { StudentAnnotatableMarkdown } from './StudentAnnotation'
import { useTestingTools, DEFAULT_TOOLS } from './TestingTools'
import { useEventTracker } from '../Markdown/hooks'

/**
 * TargetAnnotationCreator - a react component used to create annotation targets for a markdown text.
 *
 * @param {Object} props - the react props
 * @param {string} props.prompt - the prompt to display to the user
 * @param {string} props.markdown - the markdown to create annotation targets for
 * @param {Array<TargetAnnotation>} props.targetAnnotations - the current list of target annotations
 * @param {(newAnnotations: Array<TargetAnnotation>) => unknown} props.setTargetAnnotations - a function used to update the list of target annotations
 * @param {React.MutableRefObject<HTMLElement | null>} props.parentRef - a ref to the parent element for the modal
 * @param {(warning: string) => unknown} props.onWarn - a callback used to warn the user of a problem (should connect to a toast)
 *
 * @return {JSX.Element}
 */
export function TargetAnnotationCreator({
	prompt,
	markdown,
	targetAnnotations,
	setTargetAnnotations,
	onWarn,
	parentRef,
	availableAnnotationStyles = DEFAULT_TOOLS,
}: {
	prompt: string
	markdown: string
	targetAnnotations: Array<CloseAnnotationTypes.TargetAnnotation>
	setTargetAnnotations: (
		newAnnotations: Array<CloseAnnotationTypes.TargetAnnotation>
	) => unknown
	onWarn: (warning: string) => unknown
	parentRef?: React.MutableRefObject<HTMLElement | null>
	availableAnnotationStyles?: Record<
		CloseAnnotationTypes.ToolTypes,
		Array<CloseAnnotationTypes.AvailableColor>
	>
}): JSX.Element {
	const [selectedTargetIndex, setSelectedTargetIndex] = useState<null | number>(
		null
	)
	const [isCreatingNewTarget, setIsCreatingNewTarget] = useState(false)
	const [isTestingGrading, setIsTestingGrading] = useState(false)

	const selectedAnnotationTarget =
		selectedTargetIndex !== null ? targetAnnotations[selectedTargetIndex] : null

	const annotatedMarkdownComponent = (
		<AnnotatedMarkdown
			markdown={markdown}
			annotations={targetAnnotations.map(
				({ range, style }, index): Annotation => ({
					id: `r-${index}`,
					range,
					style: {
						color: style.color ?? NO_COLOR,
						tool: style.tool ?? CLOSE_READING.ANNOTATION_TOOL.HIGHLIGHT,
					},
				})
			)}
			readOnly={true}
		/>
	)

	return (
		<>
			<div
				className={
					'flex flex-row gap-2 items-center max-w-full justify-between w-full'
				}>
				<div className="flex-1 flex flex-wrap gap-2 items-center overflow-x-auto">
					{targetAnnotations.map((annotation, index) => (
						<TargetAnnotationButton
							parentRef={parentRef}
							key={`target-annotation-${index}`}
							onEdit={() => setSelectedTargetIndex(index)}
							onDelete={() =>
								setTargetAnnotations(
									targetAnnotations.filter((_, i) => i !== index)
								)
							}
							className={
								annotation.style.tool != null &&
								annotation.style.tool != CLOSE_READING.ANNOTATION_TOOL.HIGHLIGHT
									? 'border-1 border-r-0 border-black border rounded-l'
									: ''
							}
							style={getSingleAnnotationStyle(annotation)}>
							<div className="divide-x space-x-2">
								<span>Edit {index + 1}</span>
								<span className="p-2 text-xs">
									{annotation.score}pt{annotation.score > 1 ? 's' : ''}
									{annotation.required ? ' (required)' : ''}
								</span>
							</div>
						</TargetAnnotationButton>
					))}
				</div>
				<Button
					className="normal-case self-start"
					variant="secondary"
					onClick={() => {
						setIsCreatingNewTarget(true)
					}}>
					Add Annotation Target
				</Button>
				<Button
					className="normal-case self-start"
					variant="secondary"
					onClick={() => {
						setIsTestingGrading(true)
					}}>
					Test
				</Button>
			</div>
			{annotatedMarkdownComponent}
			{selectedAnnotationTarget || isCreatingNewTarget ? (
				<TargetModal
					prompt={prompt}
					annotationStyles={availableAnnotationStyles}
					parentRef={parentRef}
					// @ts-expect-error TS2322 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
					startingData={isCreatingNewTarget ? null : selectedAnnotationTarget}
					onSave={target => {
						const targetIndex = isCreatingNewTarget
							? targetAnnotations.length
							: selectedTargetIndex

						if (targetIndex != null) {
							const updatedTargets = [...targetAnnotations]
							updatedTargets[targetIndex] = target
							setTargetAnnotations(updatedTargets)
						}

						setSelectedTargetIndex(null)

						if (isCreatingNewTarget) {
							setIsCreatingNewTarget(false)
						}
					}}
					markdown={markdown}
					onWarn={onWarn}
					onCancel={() => {
						setIsCreatingNewTarget(false)
						setSelectedTargetIndex(null)
					}}
				/>
			) : null}
			{isTestingGrading ? (
				<GradeTestingModal
					prompt={prompt}
					availableAnnotationStyles={availableAnnotationStyles}
					parentRef={parentRef}
					onRequestClose={() => setIsTestingGrading(false)}
					markdown={markdown}
					targetAnnotations={targetAnnotations}
				/>
			) : null}
		</>
	)
}
/**
 * A button used to edit or delete a target annotation
 * @param {Object} props - the react props
 * @param {() => unknown} props.onEdit - a callback used to edit the target annotation
 * @param {() => unknown} props.onDelete - a callback used to delete the target annotation
 * @param {CSSProperties} props.style - the css styles to apply to the button
 * @param {ReactNode} props.children -
 * @returns
 */
function TargetAnnotationButton({
	onEdit,
	onDelete,
	style,
	className,
	children,
	parentRef,
}: {
	onEdit: () => unknown
	onDelete: () => unknown
	style?: CSSProperties
	className?: string
	children: ReactNode
	parentRef?: React.MutableRefObject<HTMLElement | null>
}) {
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
	return (
		<div className={'flex items-stretch text-black rounded overflow-hidden'}>
			<button
				onClick={onEdit}
				className={classNames(className, 'pl-2 hover:brightness-75')}
				style={style}>
				{children}
			</button>
			<button
				title="Delete"
				onClick={() => setShowDeleteConfirmation(true)}
				className="bg-error p-1 flex items-center hover:brightness-75 text-white px-2">
				<FaTrash size={12} />
			</button>
			<ConfirmationModal
				parentSelector={parentRef?.current || undefined}
				isOpen={Boolean(showDeleteConfirmation)}
				confirmText="Delete"
				onCancel={() => setShowDeleteConfirmation(false)}
				onConfirm={() => {
					setShowDeleteConfirmation(false)
					onDelete()
				}}>
				Are you sure you want to delete this target annotation: {children}?
			</ConfirmationModal>
		</div>
	)
}

/**
 * TargetModal - a modal used to creator or edit a target annotation
 *
 * @param {Object} props - the react props
 * @param {string} props.prompt - the prompt to display to the user
 * @param {string} props.markdown - the markdown that the target annotation if being created/updated for
 * @param {React.MutableRefObject<HTMLElement | null>} props.parentRef - the parent element for the modal
 * @param {TargetAnnotation | null} props.startingData - the current target annotation if updating or null if creating a new target annotation
 * @param {(targetAnnotation: TargetAnnotation) => unknown} props.onSave - a callback used to update/create the target annotation
 * @param {() => unknown} props.onCancel - a callback used to cancel creating or updating a target annotation
 * @param {(warning: string) => unknown} props.onWarn - a callback used to warn the user of a problem (should connect to a toast)
 *
 * @return {JSX.Element}
 */
function TargetModal({
	prompt,
	startingData,
	parentRef,
	onSave,
	onCancel,
	markdown,
	onWarn,
	annotationStyles,
}: {
	prompt: string
	startingData: CloseAnnotationTypes.TargetAnnotation | null
	parentRef?: React.MutableRefObject<HTMLElement | null>
	onSave: (targetAnnotation: CloseAnnotationTypes.TargetAnnotation) => unknown
	onCancel: () => unknown
	markdown: string
	onWarn: (warning: string) => unknown
	annotationStyles: Record<
		CloseAnnotationTypes.ToolTypes,
		Array<CloseAnnotationTypes.AvailableColor>
	>
}): JSX.Element {
	const [targetRange, setTargetRange] = useState(startingData?.range ?? null)
	const [bufferRange, setBufferRange] = useState(startingData?.buffer ?? null)
	const [style, setStyle] = useState(() => {
		const defaultTool: CloseAnnotationTypes.ToolTypes = Object.keys(
			annotationStyles
		)[0] as CloseAnnotationTypes.ToolTypes

		return (
			startingData?.style ?? {
				tool: defaultTool,
				color:
					(annotationStyles[defaultTool]?.[0] as
						| CloseAnnotationTypes.AvailableColor
						| null
						| undefined) ?? null,
			}
		)
	})
	const [selectingRange, setSelectingRange] = useState<'target' | 'buffer'>(
		'target'
	)
	const [isRequired, setIsRequired] = useState(startingData?.required ?? false)
	const [score, setScore] = useState(startingData?.score ?? 1)
	const eventTracker = useEventTracker()
	const [selectedAnnotationId, setSelectedAnnotationId] = useState<
		'target' | null
	>(null)

	const availableColors = style.tool
		? annotationStyles[style.tool]
		: AVAILABLE_COLORS
	const allowAnyColorOption = availableColors.length > 1

	useEffect(() => {
		const abortController = new AbortController()

		window.addEventListener('click', () => {
			if (
				eventTracker.current?.firstEventTypeHandledInCurrentEvaluationLoop !=
				null
			) {
				return
			}
			setSelectedAnnotationId(null)
		})

		return () => abortController.abort()
	}, [])

	const toolSelection = (
		<>
			<div className="flex flex-row gap-2 my-2 items-center">
				Color:{' '}
				<div className="flex flex-row justify-center items-center gap-2">
					{availableColors.map(color => (
						<SelectableCircle
							style={{
								backgroundColor: mixColors([color]) ?? undefined,
							}}
							onClick={() => {
								if (style.color === color) {
									return
								}

								setStyle(oldStyle => ({
									...oldStyle,
									color,
								}))
							}}
							selected={style.color === color}
							key={color}
						/>
					))}
					{allowAnyColorOption && (
						<SelectableCircle
							className={`flex flex-row items-center bg-gray-b justify-center text-xs text-white`}
							onClick={() => {
								if (style.color === null) {
									return
								}

								setStyle(oldStyle => ({
									...oldStyle,
									color: null,
								}))
							}}
							selected={style.color == null}>
							Any
						</SelectableCircle>
					)}
				</div>
			</div>
			<div className="flex flex-row gap-2 mb-2 items-center">
				Tool:{' '}
				<div className="flex flex-row justify-center items-center gap-2">
					{(Object.keys(
						annotationStyles
					) as CloseAnnotationTypes.ToolTypes[]).map(tool => (
						<SelectableCircle
							className={`flex flex-row items-center bg-white justify-center text-xs text-black`}
							onClick={() => {
								if (style.tool === tool) {
									return
								}
								const newToolHasAnyColorOption =
									annotationStyles[tool].length > 1
								const newToolHasCurrentColorOption = style.color
									? annotationStyles[tool].includes(style.color)
									: newToolHasAnyColorOption
								setStyle({
									color: newToolHasCurrentColorOption
										? style.color
										: newToolHasAnyColorOption
										? null
										: annotationStyles[tool][0],
									tool,
								})
							}}
							key={tool}
							selected={tool === style.tool}>
							<div
								style={getSingleAnnotationStyle({
									style: {
										tool,
										color: style.color ?? BUFFER_COLOR,
									},
								})}
								className={`!decoration-2`}>
								&nbsp;A&nbsp;
							</div>
						</SelectableCircle>
					))}
				</div>
			</div>
		</>
	)

	/**
	 * getCurrentAnnotations - formats the currently selected ranges into annotations. Will use the `updateRange` to determine
	 * the annotations if given.
	 *
	 * @param {{forSection: 'target' | 'buffer'}} updateRange? - if given, describes the new selection range for the given section, and the resulting annotations will account for that change
	 *
	 * @returns {{
	 * annotations: Annotations - annotations which can be shown in the AnnotationMarkdown
	 * ranges: { buffer: Range | null; target: Range | null } - the ranges used to calculate the annotations (will take the `updateRange` into account if given)
	 * warnings: Array<string> - any warnings generated when computing the annotations
	 * targetAnnotation: Annotation | null - the annotation which represents the target range
	 * bufferAnnotation: Annotation | null - the annotation which represents the buffer range
	 * }}
	 */
	function getCurrentAnnotations(
		updateRange?: {
			forSection: 'target' | 'buffer'
			range: Range
		} | null
	): {
		annotations: Annotations
		ranges: { buffer: Range | null; target: Range | null }
		warnings: Array<string>
		targetAnnotation: Annotation | null
		bufferAnnotation: Annotation | null
	} {
		let updatedTargetRange = targetRange
		let updatedBufferRange = bufferRange
		const warnings = []

		if (updateRange?.forSection === 'target') {
			updatedTargetRange = updateRange.range

			if (
				bufferRange &&
				getRangeIntersections(updatedTargetRange, bufferRange)
			) {
				// the target is still intersecting the buffer, update the buffer to make sure it still contains the target range
				updatedBufferRange = {
					start: Math.min(updateRange.range.start, bufferRange.start),
					end: Math.max(updateRange.range.end, bufferRange.end),
				}
			} else {
				// does not contain the target range, clear the current buffer range
				updatedBufferRange = updateRange.range
			}
		} else if (updateRange?.forSection === 'buffer') {
			if (targetRange) {
				updatedTargetRange = targetRange

				if (getRangeIntersections(updateRange.range, updatedTargetRange)) {
					updatedBufferRange = {
						start: Math.min(updateRange.range.start, targetRange.start),
						end: Math.max(updateRange.range.end, targetRange.end),
					}
				} else {
					updatedBufferRange = targetRange

					warnings.push(
						'The buffer has to be continuous and contain the target range'
					)
				}
			} else {
				warnings.push('You have to select a target range first')
			}
		}

		const updatedAnnotations: Annotations = []
		let targetAnnotation = null
		if (updatedTargetRange) {
			targetAnnotation = {
				id: 'target',
				range: updatedTargetRange,
				style: {
					tool: style.tool ?? CLOSE_READING.ANNOTATION_TOOL.HIGHLIGHT,
					color: style.color ?? NO_COLOR,
				},
			}
			updatedAnnotations.push(targetAnnotation)
		}

		let bufferAnnotation = null
		if (updatedBufferRange && updatedTargetRange) {
			bufferAnnotation = {
				id: 'buffer',
				range: updatedBufferRange,
				style: BUFFER_STYLE,
			}
			updatedAnnotations.push(
				...deleteAnnotationRange([bufferAnnotation], {
					range: updatedTargetRange,
				})
			)
		}

		return {
			annotations: updatedAnnotations,
			ranges: { target: updatedTargetRange, buffer: updatedBufferRange },
			warnings,
			bufferAnnotation,
			targetAnnotation,
		}
	}

	const currentAnnotationData = getCurrentAnnotations()

	return (
		<Modal
			isOpen={true}
			parentSelector={parentRef?.current || undefined}
			onClose={onCancel}
			closeButton={false}
			className="overflow-hidden">
			<DashboardModalHeader className="flex justify-between">
				{selectingRange === 'target' ? (
					<>
						What is the minimum acceptable target annotation?
						{targetRange && (
							<Button
								variant="secondary"
								onClick={event => {
									if (!targetRange) {
										onWarn(
											'You must select a minimum acceptable range before continuing.'
										)
										return
									}
									eventTracker.current?.noteEventType(event.type)
									setSelectingRange('buffer')
									setSelectedAnnotationId(null)
								}}>
								Next
							</Button>
						)}
					</>
				) : (
					<>
						What is the maximum acceptable target annotation?
						<Button
							onClick={event => {
								eventTracker.current?.noteEventType(event.type)
								setSelectingRange('target')
								setSelectedAnnotationId('target')
							}}>
							Back
						</Button>
						<Button
							variant="secondary"
							className="ml-2"
							onClick={() => {
								if (!targetRange || !bufferRange) {
									onWarn(
										'You must select a minimum and maximum acceptable range before continuing.'
									)
									return
								}
								setSelectedAnnotationId(null)
								onSave({
									score,
									required: isRequired,
									range: targetRange,
									buffer: bufferRange,
									style,
								})
							}}>
							Save
						</Button>
					</>
				)}
			</DashboardModalHeader>
			<DashboardModalBody className="flex flex-col justify-between py-2">
				<div className="my-2">
					Prompt:&nbsp;
					<span className="my-2 bg-white/20 w-fit p-1 rounded text-base leading-8">
						{prompt}
					</span>
				</div>
				<div className="flex flex-row justify-between py-2 items-center">
					<Button
						onClick={() => {
							if (selectingRange === 'target') {
								setTargetRange(null)
								setBufferRange(null)
								return
							}
							setBufferRange(targetRange)
						}}>
						Clear Selection
					</Button>

					<label>
						<input
							type="checkbox"
							checked={isRequired}
							onChange={e => setIsRequired(e.target.checked)}
						/>{' '}
						required
					</label>
					<label>
						Score{' '}
						<input
							type="number"
							className="text-black pl-2 rounded"
							value={score}
							onChange={e => {
								try {
									const number = Number(e.target.value)
									if (Number.isNaN(number) || !Number.isFinite(number)) {
										return
									}
									setScore(number)
								} catch (e) {
									return
								}
							}}
						/>
					</label>
				</div>
				<AnnotatedMarkdown
					className=" max-h-[70%] p-4 px-10 overflow-y-auto rounded bg-white"
					readOnly={false}
					onRangeSelection={({ range }) => {
						const updateData = getCurrentAnnotations({
							forSection: selectingRange,
							range,
						})

						updateData.warnings.forEach(warning => onWarn(warning))

						setTargetRange(updateData.ranges.target)
						setBufferRange(updateData.ranges.buffer)

						if (selectingRange === 'target') {
							setSelectedAnnotationId(selectingRange)
						}
					}}
					onRangeSelectionPreview={({ range }) => {
						if (selectingRange === 'target') {
							const previewTargetAnnotation: Annotation = {
								id: 'preview-target-annotation',
								style: {
									tool: style.tool ?? CLOSE_READING.ANNOTATION_TOOL.HIGHLIGHT,
									color: style.color ?? NO_COLOR,
								},
								range,
							}

							const result = [previewTargetAnnotation]

							if (currentAnnotationData.bufferAnnotation) {
								result.push(
									...deleteAnnotationRange(
										[currentAnnotationData.bufferAnnotation],
										{ range }
									)
								)
							}

							return result
						}
						if (
							selectingRange === 'buffer' &&
							currentAnnotationData.targetAnnotation
						) {
							const updatedBufferAnnotation: Annotation = {
								id: 'buffer',
								style: BUFFER_STYLE,
								range,
							}

							if (targetRange) {
								return [
									currentAnnotationData.targetAnnotation,
									...deleteAnnotationRange([updatedBufferAnnotation], {
										range: targetRange,
									}),
								] as Annotations
							}

							return [updatedBufferAnnotation]
						}
						return [
							{ id: 'this-should-not-be-possible', range, style: BUFFER_STYLE },
						]
					}}
					markdown={markdown}
					annotations={currentAnnotationData.annotations}
					eventTrackerRef={eventTracker}
					onAnnotationRangeUpdate={(annotationId, rangeSelection) => {
						if (!(annotationId === 'buffer' || annotationId === 'target')) {
							return
						}
						const updateData = getCurrentAnnotations({
							forSection: annotationId,
							range: rangeSelection.range,
						})

						setTargetRange(updateData.ranges.target)
						setBufferRange(updateData.ranges.buffer)

						if (annotationId === 'target') {
							setSelectedAnnotationId(annotationId)
						}
					}}
					onAnnotationRangeUpdatePreview={(annotationId, rangeSelection) =>
						getCurrentAnnotations(
							annotationId === 'buffer' || annotationId === 'target'
								? {
										forSection: annotationId,
										range: rangeSelection.range,
								  }
								: null
						).annotations
					}
					selectedAnnotationId={selectedAnnotationId}
				/>
				<div className="flex flex-row justify-between gap-4 p-2 items-baseline">
					{toolSelection}
				</div>
			</DashboardModalBody>
		</Modal>
	)
}

/**
 * SelectableCircle - a circle which will show a check icon to its bottom right when selected
 *
 * @param {Object} props - the react props
 * @param {boolean} props.selected - true if the option the circle represents is selected, false otherwise
 * @param {() => unknown} props.onClick - an onClick listener for the circle
 * @param {React$Node} props.children? - the children to put inside of the circle
 * @param {string} props.className? - any css class names to apply to the circle
 * @param {CSSProperties} props.style? - any css styles to apply to the circle
 *
 * @return {JSX.Element}
 */
export function SelectableCircle({
	className,
	selected,
	children,
	...rest
}: {
	selected: boolean
	onClick: () => unknown
	className?: string
	style?: CSSProperties
	children?: ReactNode
	disabled?: boolean
}): JSX.Element {
	return (
		<button
			className={classNames(
				className,
				'min-w-6 max-w-6 min-h-6 max-h-6 rounded-full cursor-pointer relative disabled:!cursor-not-allowed'
			)}
			{...rest}>
			{selected ? (
				<div className="rounded-full bg-white absolute bottom-0 right-0 text-success p-[2px] text-xs translate-x-1/2 translate-y-1/4">
					<FaCheckCircle />
				</div>
			) : null}
			{children}
		</button>
	)
}

/**
 * GradeTestingModal - a modal used to test the grading of student annotations against the given target annotations
 *
 * @param {Object} props - the react props
 * @param {string} props.prompt - the prompt to display to the user
 * @param {string} props.markdown - the markdown the student should annotate
 * @param {Array<CloseAnnotationTypes.TargetAnnotation>} props.targetAnnotations - the target annotations to grade against
 * @param {() => unknown} props.onRequestClose - a callback for when the user wants to stop testing the grading
 *
 * @return {JSX.Element}
 */
function GradeTestingModal({
	markdown,
	prompt,
	targetAnnotations,
	onRequestClose,
	parentRef,
	availableAnnotationStyles,
}: {
	markdown: string
	prompt: string
	targetAnnotations: Array<CloseAnnotationTypes.TargetAnnotation>
	onRequestClose: () => unknown
	parentRef?: React.MutableRefObject<HTMLElement | null>
	availableAnnotationStyles: Record<
		CloseAnnotationTypes.ToolTypes,
		Array<CloseAnnotationTypes.AvailableColor>
	>
}): JSX.Element {
	const [studentAnnotations, setStudentAnnotations] = useState<Annotations>([])
	const maximumGrade = useMemo(
		() =>
			targetAnnotations.reduce(
				(sum, targetAnnotation) => (sum += targetAnnotation.score),
				0
			),
		[targetAnnotations]
	)
	const rangeTransformer = useMemo(
		() =>
			CLOSE_ANNOTATION_HELPERS.rangeTransformers.selectWholeElementRangeTransformer(
				CLOSE_ANNOTATION_HELPERS.selectableRangesGenerators.getVisibleWordsFromAst(
					CLOSE_ANNOTATION_HELPERS.parseMarkdownToAST(markdown)
				)
			),
		[markdown]
	)

	const currentGrade = useMemo(
		() =>
			CLOSE_ANNOTATION_HELPERS.gradeAnnotations(
				targetAnnotations,
				// @ts-expect-error ts(2345) the color type on annotations in `question-views` is a string, while in `question-toolkit` it is an Enum. This difference is to handle some transformation needed in the rendering step.
				studentAnnotations,
				rangeTransformer
			),
		[targetAnnotations, studentAnnotations, rangeTransformer]
	)
	const { currentAnnotationInformation, toolSelector } = useTestingTools(
		availableAnnotationStyles
	)
	return (
		<Modal
			isOpen={true}
			onClose={onRequestClose}
			closeButton={false}
			parentSelector={parentRef?.current || undefined}>
			<DashboardModalHeader className="flex justify-between">
				<div>
					Grade: {currentGrade?.score ?? 'NONE'} / {maximumGrade}
				</div>
				<div>
					All Required Targets Satisfied:{' '}
					{currentGrade == null || currentGrade.allRequiredTargetsSatisfied
						? 'Yes'
						: 'No'}
				</div>
			</DashboardModalHeader>
			<DashboardModalBody>
				<div className="my-2">
					Prompt:&nbsp;
					<span className="my-2 bg-white/20 w-fit p-1 rounded text-base leading-8">
						{prompt}
					</span>
				</div>
				<div className="text-white mb-2">{toolSelector}</div>
				<StudentAnnotatableMarkdown
					markdown={markdown}
					setAnnotations={setStudentAnnotations}
					annotations={studentAnnotations}
					annotationInformation={currentAnnotationInformation}
					className="bg-white p-2"
				/>
			</DashboardModalBody>
		</Modal>
	)
}

const BUFFER_STYLE = {
	tool: CLOSE_READING.ANNOTATION_TOOL.HIGHLIGHT,
	color: BUFFER_COLOR,
}
